export const NOROSHI_RESERVE_TAG = "noroshi-reserve";
export const NOROSHI_RESERVE_SCRIPT_PATH = "/customer-ui/noroshi-reserve.js";
export const OPEN_RESERVE_QUERY = "open_reserve";
export const DEFAULT_NOROSHI_ORIGIN = "https://noroshi.hairbook.jp";
/** WC-owned localStorage key. Pages read the sessionId only; booking PII stays in the WC. */
export const NOROSHI_RESERVE_SESSION_STORAGE_KEY = "noroshi.reserve.session.v1";
export const NOROSHI_RESERVE_RESUME_LABEL = "予約を続ける";
export const NOROSHI_RESERVE_START_LABELS = ["Web予約する", "このメニューで予約する"] as const;

const NOROSHI_RESERVE_SESSION_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export type NoroshiReserveSessionSignal = {
  sessionId: string;
  salonId?: string;
  step?: string;
  cancelled?: boolean;
};

export type NoroshiReserveElement = HTMLElement & {
  open(): void;
  close(): void;
  minimize(): void;
  expand(): void;
  reset(): void;
  readonly isOpen: boolean;
  readonly isMinimized?: boolean;
  readonly session?: NoroshiReserveSessionSignal | null;
};

export type ReservationRedirectParams = {
  salonId: string;
  menuId?: string | null;
  staffId?: string | null;
  mode?: string | null;
};

let loadPromise: Promise<void> | null = null;

export function noroshiOrigin(): string {
  const env = import.meta.env as { VITE_NOROSHI_ORIGIN?: string } | undefined;
  return (env?.VITE_NOROSHI_ORIGIN || DEFAULT_NOROSHI_ORIGIN).replace(/\/$/, "");
}

export function noroshiReserveScriptUrl(origin = noroshiOrigin()): string {
  return `${origin.replace(/\/$/, "")}${NOROSHI_RESERVE_SCRIPT_PATH}`;
}

export function isNoroshiReserveEnabled(value: boolean | number | null | undefined): boolean {
  return value === true || value === 1;
}

export function reservationRedirectHref({
  salonId,
  menuId,
  staffId,
  mode,
}: ReservationRedirectParams): string {
  const params = new URLSearchParams({ to: "reserve", salon_id: String(salonId) });
  if (menuId) params.set("menu_id", menuId);
  if (staffId) params.set("staff_id", staffId);
  if (mode) params.set("mode", mode);
  return `/redirect?${params.toString()}`;
}

export function shouldAutoOpenNoroshiReserve(
  search = typeof window === "undefined" ? "" : window.location.search,
): boolean {
  return new URLSearchParams(search).get(OPEN_RESERVE_QUERY) === "1";
}

export function salonpageReserveHref({
  salonId,
  menuId,
  staffId,
  mode,
}: ReservationRedirectParams): string {
  const params = new URLSearchParams({ [OPEN_RESERVE_QUERY]: "1" });
  const encodedSalonId = encodeSalonpagePathId(salonId);
  if (mode === "overview") {
    return `/salons/${encodedSalonId}/overview?${params}`;
  }
  if (menuId) {
    if (staffId) params.set("staff_id", staffId);
    return `/salons/${encodedSalonId}/menus/${encodeSalonpagePathId(menuId)}?${params}`;
  }
  if (staffId) {
    return `/staffs/${encodeSalonpagePathId(staffId)}?${params}`;
  }
  return `/salons/${encodedSalonId}?${params}`;
}

export function noroshiReserveGatewayOrigin(
  input: { host?: string; origin?: string; override?: string | null } = {},
): string {
  const override = String(input.override ?? viteGatewayOriginOverride() ?? "").replace(/\/$/, "");
  if (override && !isAccountsHost(originHost(override))) return override;

  if (input.origin) return input.origin.replace(/\/$/, "");
  if (typeof window !== "undefined") return window.location.origin;
  return "";
}

export function noroshiReserveSalonpageOrigin(
  origin = typeof window === "undefined" ? "" : window.location.origin,
): string {
  try {
    const url = new URL(origin);
    if (url.protocol !== "https:") return "";
    if (
      url.hostname === "salonpage.hairbook.jp" ||
      url.hostname === "salonpage.hairbook.dev"
    ) {
      return `https://${url.hostname}`;
    }
  } catch {
    return "";
  }
  return "";
}

export function applyNoroshiReserveAttributes(
  element: HTMLElement,
  salonId: string,
  origins: { apiBase?: string; gatewayOrigin?: string; salonpageOrigin?: string } = {},
): void {
  const apiBase = (origins.apiBase ?? noroshiOrigin()).replace(/\/$/, "");
  if (apiBase) element.setAttribute("api-base", apiBase);
  else element.removeAttribute("api-base");

  const gatewayOrigin = origins.gatewayOrigin ?? noroshiReserveGatewayOrigin();
  if (gatewayOrigin) element.setAttribute("gateway-origin", gatewayOrigin);
  else element.removeAttribute("gateway-origin");

  const salonpageOrigin = origins.salonpageOrigin ?? noroshiReserveSalonpageOrigin();
  if (salonpageOrigin) element.setAttribute("salonpage-origin", salonpageOrigin);
  else element.removeAttribute("salonpage-origin");

  // salon-id last: WC restore (#s / GET session) runs on this attribute and
  // 404-clears the session key if api-base is still empty.
  element.setAttribute("salon-id", salonId);
}

export function loadNoroshiReserve(scriptUrl = noroshiReserveScriptUrl()): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (customElements.get(NOROSHI_RESERVE_TAG)) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = import(/* @vite-ignore */ scriptUrl)
      .then(() => undefined)
      .catch((error) => {
        loadPromise = null;
        throw error;
      });
  }
  return loadPromise;
}

export function defaultNoroshiReserveStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readNoroshiReserveSessionId(
  storage: Storage | null = defaultNoroshiReserveStorage(),
): string | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(NOROSHI_RESERVE_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  const trimmed = raw.trim();
  if (isNoroshiReserveSessionId(trimmed)) return trimmed;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === "string" && isNoroshiReserveSessionId(parsed)) return parsed;
    if (parsed && typeof parsed === "object") {
      const sessionId = (parsed as { sessionId?: unknown }).sessionId;
      if (typeof sessionId === "string" && isNoroshiReserveSessionId(sessionId)) return sessionId;
    }
  } catch {
    return null;
  }
  return null;
}

export function hasInProgressNoroshiReserveSession(
  storage: Storage | null = defaultNoroshiReserveStorage(),
): boolean {
  return readNoroshiReserveSessionId(storage) != null;
}

export function rewriteNoroshiReserveCtaText(text: string, inProgress: boolean): string {
  if (!inProgress) return text;
  let next = text;
  for (const label of NOROSHI_RESERVE_START_LABELS) {
    next = next.replaceAll(label, NOROSHI_RESERVE_RESUME_LABEL);
  }
  return next;
}

export function subscribeNoroshiReserveSession(
  onChange: (inProgress: boolean) => void,
  options: {
    storage?: Storage | null;
    target?: Pick<EventTarget, "addEventListener" | "removeEventListener"> | null;
    intervalMs?: number;
  } = {},
): () => void {
  const storage = "storage" in options ? (options.storage ?? null) : defaultNoroshiReserveStorage();
  const target =
    options.target === undefined
      ? typeof window === "undefined"
        ? null
        : window
      : options.target;
  const intervalMs = options.intervalMs ?? 500;

  let current = hasInProgressNoroshiReserveSession(storage);
  onChange(current);

  const notify = () => {
    const next = hasInProgressNoroshiReserveSession(storage);
    if (next === current) return;
    current = next;
    onChange(next);
  };

  const onStorage = (event: Event) => {
    const key = "key" in event ? (event as StorageEvent).key : null;
    if (key != null && key !== NOROSHI_RESERVE_SESSION_STORAGE_KEY) return;
    notify();
  };

  target?.addEventListener("storage", onStorage);
  target?.addEventListener("focus", notify);

  const timer =
    intervalMs > 0 && typeof window !== "undefined" ? window.setInterval(notify, intervalMs) : null;

  return () => {
    target?.removeEventListener("storage", onStorage);
    target?.removeEventListener("focus", notify);
    if (timer != null) window.clearInterval(timer);
  };
}

export function shouldMountNoroshiReserve(input: {
  enabled?: boolean | number | null;
  autoOpen?: boolean;
  storage?: Storage | null;
} = {}): boolean {
  if (hasInProgressNoroshiReserveSession(input.storage)) return true;
  return isNoroshiReserveEnabled(input.enabled) && Boolean(input.autoOpen);
}

export function isNoroshiReserveInProgress(element: NoroshiReserveElement): boolean {
  if (element.isMinimized) return true;
  const session = element.session;
  if (!session?.sessionId) return false;
  if (session.cancelled) return false;
  if (session.step === "done") return false;
  return true;
}

export function expandNoroshiReserveElement(element: NoroshiReserveElement): "noop" | "expand" {
  if (element.isOpen) return "noop";
  element.expand();
  return "expand";
}

export function resumeNoroshiReserveElement(
  element: NoroshiReserveElement,
  storage: Storage | null = defaultNoroshiReserveStorage(),
): "noop" | "expand" | "open" {
  if (element.isOpen) return "noop";
  if (isNoroshiReserveInProgress(element) || hasInProgressNoroshiReserveSession(storage)) {
    return expandNoroshiReserveElement(element);
  }
  element.open();
  return "open";
}

export function ensureNoroshiReserveElement(
  salonId: string,
  documentRef: Document = document,
): NoroshiReserveElement {
  const existing = documentRef.querySelector(NOROSHI_RESERVE_TAG);
  const element = (existing ?? documentRef.createElement(NOROSHI_RESERVE_TAG)) as NoroshiReserveElement;
  applyNoroshiReserveAttributes(element, salonId);
  if (!existing) documentRef.body.appendChild(element);
  return element;
}

export async function mountNoroshiReserve(salonId: string): Promise<NoroshiReserveElement> {
  await loadNoroshiReserve();
  return ensureNoroshiReserveElement(salonId, document);
}

export async function expandNoroshiReserve(salonId: string): Promise<NoroshiReserveElement> {
  const element = await mountNoroshiReserve(salonId);
  expandNoroshiReserveElement(element);
  return element;
}

export async function openNoroshiReserve(salonId: string): Promise<NoroshiReserveElement> {
  const element = await mountNoroshiReserve(salonId);
  resumeNoroshiReserveElement(element);
  return element;
}

function isNoroshiReserveSessionId(value: string): boolean {
  return NOROSHI_RESERVE_SESSION_ID_PATTERN.test(value);
}

function viteGatewayOriginOverride(): string | undefined {
  const env = import.meta.env as { VITE_GATEWAY_ORIGIN?: string } | undefined;
  const value = env?.VITE_GATEWAY_ORIGIN;
  return value ? String(value) : undefined;
}

function originHost(origin: string): string {
  try {
    return new URL(origin).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function isAccountsHost(host: string): boolean {
  return host === "accounts.hairbook.jp" || host === "accounts.hairbook.dev";
}

function encodeSalonpagePathId(value: string | number): string {
  return encodeURIComponent(String(value)).replace(/%40/gi, "@");
}
